import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { OpenInNew, Storefront } from '@mui/icons-material';
import apiService from '../services/apiService';
import { API_ENDPOINTS } from '../config/api';
import { useAuth } from '../contexts/AuthContext';

interface Branch {
  _id: string;
  studioName: string;
  branchName?: string | null;
  branchCode?: string | null;
  email?: string;
  phoneNumber?: string;
  city?: string;
  address?: string;
  isActive: boolean;
  branchIsActive?: boolean;
}

interface BranchesTabProps {
  studioId: string;
  studioName: string;
  /** The brand this studio belongs to, from the studio detail response. */
  brand?: { _id: string; name: string; slug?: string; appSource?: string } | null;
}

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  THE OTHER LOCATIONS OF THIS BUSINESS — read only
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * This used to be a 750-line management surface: create a branch, deactivate
 * one, delete one. All three called endpoints keyed on a PARENT STUDIO id, and
 * all three are gone — a branch belongs to a BRAND now, not to another shop.
 *
 * What is left is the view a person standing on one studio actually wants:
 * "what else is in this business, and where do I go to change it". The
 * managing happens on the brand, at /brands/:id, which is also the only place
 * that can express a brand-wide grant.
 *
 * It is also now symmetric. Before, a parent saw its branches and a branch saw
 * nothing — the tab was hidden outright for any studio with a `parentStudio`.
 * A brand has no direction, so every location sees the same list.
 */
const BranchesTab: React.FC<BranchesTabProps> = ({ studioId, studioName, brand }) => {
  const navigate = useNavigate();
  const { admin } = useAuth();
  const isSuperAdmin = admin?.role === 'super_admin';

  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res: any = await apiService.get(API_ENDPOINTS.STUDIOS.BRANCHES(studioId));
      setBranches(res?.branches ?? []);
    } catch (e: any) {
      setError(e.message || 'Could not load the other locations');
    } finally {
      setLoading(false);
    }
  }, [studioId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h6">
            {brand ? brand.name : studioName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {branches.length === 0
              ? 'This is the only location.'
              : `${branches.length + 1} locations, including this one.`}
          </Typography>
        </Box>
        {isSuperAdmin && brand && (
          <Button
            variant="outlined"
            endIcon={<OpenInNew />}
            onClick={() => navigate(`/brands/${brand._id}`)}
          >
            Manage brand
          </Button>
        )}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!brand && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This studio does not belong to a brand yet, so it has no other
          locations. A super admin can create one from the Brands page.
        </Alert>
      )}

      {branches.length === 0 ? (
        <Card variant="outlined">
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <Storefront sx={{ fontSize: 44, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">
              No other locations.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {branches.map((b) => (
            <Grid key={b._id} size={{ xs: 12, sm: 6, md: 4 }}>
              <Card variant="outlined" sx={{ opacity: b.branchIsActive === false ? 0.6 : 1 }}>
                <CardContent>
                  <Typography fontWeight={600} noWrap>
                    {b.branchName || b.studioName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap display="block">
                    {b.studioName}
                    {b.branchCode ? ` · ${b.branchCode}` : ''}
                  </Typography>
                  {(b.address || b.city) && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                      {[b.address, b.city].filter(Boolean).join(', ')}
                    </Typography>
                  )}
                  <Stack direction="row" spacing={0.5} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                    {b.branchIsActive === false && <Chip size="small" label="Inactive" />}
                    <Chip size="small" variant="outlined" label={b.email ? 'Own login' : 'No login'} />
                  </Stack>
                  <Button size="small" sx={{ mt: 1 }} onClick={() => navigate(`/studios/${b._id}`)}>
                    Open
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default BranchesTab;
